import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators'
import { Provinces } from '../app-config.constants';
import { Helper } from '../classes/helper';

interface PostalCodeData {
  province: string,
  target: string[]
}

/**
 * A service for checking whether a postal code is targetted for advertising
 * Note that data is stored in a format that minimizes file size by grouping to avoid repeating data
 */
@Injectable()
export class AdPostalCodeService {

  private readonly postalCodeMap$: Observable<Map<string, PostalCodeData>>;

  constructor(
    private http: HttpClient
  ) {
    // set up observable to process the JSON once and share that replay with future subscribers (cache for life of the service)
    this.postalCodeMap$ = this.http.get(this.getPostalCodeFileEndpoint())
      .pipe(
        map(data => {
          const postalCodes = new Map<string, PostalCodeData>();
          for (const target in (data as any)) {
            for (const province in data[target]) {
              data[target][province]['FSAs'].forEach((fsa: string) => {
                const currentValue = postalCodes.get(fsa);
                // handle multiple target types for one postal code
                if (currentValue !== undefined) {
                  postalCodes.set(fsa, { province: province, target: currentValue.target.concat(target) })
                }
                else {
                  postalCodes.set(fsa, { province: province, target: [target] });
                }
              })
            }
          };
          return postalCodes;
        }),
        shareReplay(1),
        catchError(() => of(new Map<string, PostalCodeData>())));
  }

  // return a promise that resolves to the postal code's province
  async getProvince(postalCode: string): Promise<string> {
    const provinceShort = await this.getPostalCodeData(postalCode)
                                      .then(postalCodeData => postalCodeData ? postalCodeData.province : null);
    const province =  Provinces.find(p => p.short === provinceShort);
    return province ? Helper.replaceWhitespace(province.long, '') : null;
  }

  // return a promise that resolves to the postal code's target
  async getTarget(postalCode: string): Promise<string[]> {
    return await this.getPostalCodeData(postalCode)
                      .then(postalCodeData => postalCodeData ? postalCodeData.target : null);
  }

  private async getPostalCodeData(postalCode: string): Promise<PostalCodeData> {
    return await this.postalCodeMap$.toPromise().then(postalCodes => postalCodes.get(this.getFSA(postalCode)));
  }

  // get the 3 char FSA from a postal code
  private getFSA(postalCode: string): string {
    if (postalCode.length < 3)
      return postalCode;
    return postalCode.substring(0, 3);
  }

  private getPostalCodeFileEndpoint(): string {
    return Helper.getStaticPathForResource('/assets/json/bell/ad-postal-codes-v1.json');
  }

}
